import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import LeadForm from '../components/LeadForm';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      setIsSubmitted(true);
      toast.success('Your message has been sent successfully!');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#2E5C31]" data-testid="contact-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-['Poppins']">
            Contact Us
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
            Have questions or ready to get started? We're here to help with all your landscaping needs.
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-8 font-['Poppins']">
                Get In Touch
              </h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] mb-1">Our Location</p>
                    <p className="text-[#4A4A4A]">667 Harbor View Drive<br />Seattle, WA 98101</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] mb-1">Phone</p>
                    <a href="tel:+18888808889" className="text-[#2E5C31] hover:underline text-lg">+1-888-880-8889</a>
                    <p className="text-[#888888] text-sm mt-1">Click to call</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] mb-1">Email</p>
                    <a href="mailto:support@decorvista.store" className="text-[#2E5C31] hover:underline">support@decorvista.store</a>
                    <p className="text-[#888888] text-sm mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] mb-1">Business Hours</p>
                    <p className="text-[#4A4A4A]">Monday - Saturday: 9:00 AM - 6:00 PM</p>
                    <p className="text-[#4A4A4A]">Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* General Contact Form */}
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md">
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-6 font-['Poppins']">
                  Send Us a Message
                </h3>
                
                {isSubmitted ? (
                  <div className="text-center py-8" data-testid="contact-form-success">
                    <div className="w-16 h-16 bg-[#2E5C31]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-[#2E5C31]" />
                    </div>
                    <h4 className="text-lg font-semibold text-[#1A1A1A] mb-2">Message Sent!</h4>
                    <p className="text-[#4A4A4A] mb-4">We'll get back to you within 24 hours.</p>
                    <Button
                      onClick={() => {
                        setIsSubmitted(false);
                        setContactForm({
                          name: '',
                          email: '',
                          phone: '',
                          subject: '',
                          message: '',
                        });
                      }}
                      variant="outline"
                      className="border-[#2E5C31] text-[#2E5C31]"
                      data-testid="send-another-message-btn"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="text-[#1A1A1A] font-medium">
                          Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={contactForm.name}
                          onChange={handleInputChange}
                          placeholder="Your name"
                          className="mt-2 rounded-lg"
                          data-testid="contact-input-name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-[#1A1A1A] font-medium">
                          Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={contactForm.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          className="mt-2 rounded-lg"
                          data-testid="contact-input-email"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="phone" className="text-[#1A1A1A] font-medium">
                          Phone (Optional)
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={contactForm.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          className="mt-2 rounded-lg"
                          data-testid="contact-input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-[#1A1A1A] font-medium">
                          Subject <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={contactForm.subject}
                          onChange={handleInputChange}
                          placeholder="How can we help?"
                          className="mt-2 rounded-lg"
                          data-testid="contact-input-subject"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-[#1A1A1A] font-medium">
                        Message <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={contactForm.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your inquiry..."
                        className="mt-2 min-h-[120px] rounded-lg"
                        data-testid="contact-input-message"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#2E5C31] hover:bg-[#244A26] text-white rounded-full py-3 font-semibold"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Lead Capture Form */}
            <div>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
